import { Modal } from "@shared/ui";
import { getGoogleSheetUrl } from "../../lib/constants";
import styles from "./styles.module.scss";
import { ProcessFooter } from "./ui/ProcessFooter";
import { ProcessStep } from "./ui/ProcessStep";
import { UploadFooter, UploadStep } from "./ui/UploadStep";
import { useBulkAddModal } from "./useBulkAddModal";

interface BulkAddModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function BulkAddModal({
  isOpen,
  onClose,
  onSuccess,
}: BulkAddModalProps) {
  const {
    briefId,
    step,
    setStep,
    reviewTab,
    setReviewTab,
    parsedRows,
    isSyncing,
    isDownloadingXlsx,
    isParsingCreatorsCsv,
    isBulkActions,
    deliverables,
    hasNoDeliverables,
    configurators,
    customFields,
    templateSheetId,
    processedRows,
    downloadBriefXlsx,
    parseCreatorsCsv,
    handleDeliverableCreated,
    handleImport,
  } = useBulkAddModal({ isOpen, onClose, onSuccess });

  const isProcessMode = step === "process";

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      title={isProcessMode ? "Review import" : "Bulk Add Creators"}
      footer={null}
      width={isProcessMode ? 920 : 600}
      className={styles.modal}
      destroyOnClose
      styles={{
        body: { padding: 0 },
        header: { marginBottom: 0, padding: 20 },
        content: { padding: 0 },
      }}
    >
      <div className={styles.modalContent}>
        {isProcessMode ? (
          <>
            <ProcessStep
              rows={parsedRows}
              deliverables={deliverables ?? []}
              configurators={configurators}
              customFields={customFields}
              activeTab={reviewTab}
              onTabChange={setReviewTab}
            />
            <ProcessFooter
              processedCount={processedRows.length}
              skippedCount={parsedRows.length - processedRows.length}
              reviewTab={reviewTab}
              isImporting={isBulkActions}
              onBack={() => {
                setReviewTab("processed");
                setStep("upload");
              }}
              onViewSkipped={() => setReviewTab("error")}
              onImport={handleImport}
            />
          </>
        ) : (
          <>
            <div className={styles.modalBody}>
              <UploadStep
                briefId={briefId}
                templateSheetId={templateSheetId}
                isSyncing={isSyncing}
                isParsing={isParsingCreatorsCsv}
                isDownloadingXlsx={isDownloadingXlsx}
                hasNoDeliverables={hasNoDeliverables}
                onDeliverableCreated={handleDeliverableCreated}
                onDownloadXlsx={() => {
                  if (!briefId) return;
                  downloadBriefXlsx({ briefId });
                }}
                onDownloadTemplate={() => {
                  if (!templateSheetId) return;
                  window.open(getGoogleSheetUrl(templateSheetId), "_blank");
                }}
                onCsvUploaded={(csvUrl) => {
                  parseCreatorsCsv({ briefId, csv: csvUrl });
                }}
              />
            </div>
            <UploadFooter onClose={onClose} />
          </>
        )}
      </div>
    </Modal>
  );
}
